import type { CreateUniLoginHostedAuthRequestResponse } from '@unipile/types';
import type { TFunction } from 'i18next';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLoginWizardDescription } from '../../../hooks/useLoginWizardDescription';
import { useProvider } from '../../../hooks/useProvider';
import type { UniLoginExtensionWarning, UniLoginFlowState } from '../../../hooks/useUniLoginFlow';
import type { ExtensionBrowser } from '../../../libs/unilogin-bridge';
import { Alert, AlertDescription, AlertTitle } from '../../shadcn/Alert';
import { Button } from '../../shadcn/Button';
import { UniLoginAccountMismatch } from './UniLoginAccountMismatch';
import { UniLoginClosed } from './UniLoginClosed';
import { UniLoginExtensionMissing } from './UniLoginExtensionMissing';
import { UniLoginLoading } from './UniLoginLoading';
import { UniLoginProgress } from './UniLoginProgress';
import { UniLoginLayout } from './screen-layout';

export type UniLoginScreenProps = {
  state: UniLoginFlowState;
  /** Undefined until the UniLogin request is created. */
  request: CreateUniLoginHostedAuthRequestResponse | undefined;
  browser: ExtensionBrowser | null;
  publisherName: string | undefined;
  onRetry: () => void;
  onRestart: () => void;
  onCancel: () => void;
};

export function UniLoginScreen({ state, request, browser, publisherName, onRetry, onRestart, onCancel }: UniLoginScreenProps) {
  const { t } = useTranslation();
  const { provider_name } = useProvider();
  useLoginWizardDescription(
    t(publisherName ? 'LoginScreen.unilogin.description_with_publisher' : 'LoginScreen.unilogin.description', {
      ns: 'translation',
      provider: provider_name,
      publisher: publisherName,
    }),
  );

  const { status, extension_warning } = state;
  if (status === 'starting') return <UniLoginLoading detecting={request !== undefined} />;
  if (status === 'declined' || status === 'expired' || status === 'error') {
    return <UniLoginClosed status={status} requestCreated={request !== undefined} onRestart={onRestart} onCancel={onCancel} />;
  }

  return (
    <>
      {status === 'extension_missing' ? (
        <UniLoginExtensionMissing browser={browser} request={request} />
      ) : status === 'account_mismatch' ? (
        <UniLoginAccountMismatch onRetry={onRetry} />
      ) : extension_warning ? (
        <UniLoginLayout
          actions={
            <>
              <Button variant="outline" type="button" onClick={onRetry}>
                {t('LoginScreen.unilogin.retry', { ns: 'translation', provider: provider_name })}
              </Button>
            </>
          }
        >
          <Alert variant="red">
            <CircleAlert />
            <AlertTitle>
              {t(
                extension_warning.type === 'bridge_unavailable'
                  ? 'LoginScreen.unilogin.extension_unreachable.title'
                  : 'LoginScreen.unilogin.sync_failed.title',
                { ns: 'translation' },
              )}
            </AlertTitle>
            <AlertDescription>{extensionWarningMessage(t, extension_warning, provider_name)}</AlertDescription>
          </Alert>
        </UniLoginLayout>
      ) : (
        <UniLoginProgress status={status} backendRetrying={state.backend_retrying} onRetry={onRetry} />
      )}
    </>
  );
}

function extensionWarningMessage(t: TFunction, warning: UniLoginExtensionWarning, provider: string): string {
  const options = { ns: 'translation', provider };
  if (warning.type === 'bridge_unavailable') return t('LoginScreen.unilogin.extension_retry_failed', options);
  const { code } = warning;
  if (code === 'web_access_denied') return t('LoginScreen.unilogin.extension_web_access_denied', options);
  if (code === 'account_tab_timeout' || code === 'account_tab_unavailable') {
    return t('LoginScreen.unilogin.extension_tab_unavailable', options);
  }
  if (code === 'secure_upload_failed' || code === 'sync_failed') return t('LoginScreen.unilogin.extension_sync_failed', options);
  return code && /^[a-z0-9_]+$/.test(code)
    ? t('LoginScreen.unilogin.extension_error_code', { ...options, code })
    : t('LoginScreen.unilogin.extension_error', options);
}
